import * as React from "react"
import { gql, useApolloClient } from "@apollo/client"
import { Button, Stack, useDisclosure } from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useRouter } from "next/router"

import { ACCESS_TOKEN, REDIRECT_PATH, REFRESH_TOKEN_KEY } from "lib/config"
import { MeDocument, RegisterInput, useGetUpcomingFinalDateQuery, useRegisterMutation } from "lib/graphql"
import { useForm } from "lib/hooks/useForm"
import yup from "lib/yup"
import { Form } from "components/Form"
import { FormError } from "components/FormError"
import { Input } from "components/Input"
import { Modal } from "components/Modal"
import { RegisterExtraInfoForm } from "components/RegisterExtraInfoForm"

import { regions } from "../lib/static/filters"
import { categoryOptions, howDidYourHearMultiOptions, pronounOptions } from "../lib/static/options"
import { DateInput } from "./DateInput"
import { MultiSelect } from "./MultiSelect"
import { RegisterAgeCheckMessage } from "./RegisterAgeCheckMessage"
import { RegisterExtraInfo } from "./RegisterExtraInfoForm"
import { Select } from "./Select"

dayjs.extend(utc)

const _ = gql`
  mutation Register($data: RegisterInput!) {
    register(data: $data) {
      user {
        ...Me
      }
      token
      refreshToken
    }
  }
`

const RegisterSchema = yup.object().shape({
  email: yup.string().email("Ongeldig e-mailadres").required("Verplicht"),
  password: yup.string().min(8, "Moet ten minste 8 karakaters bevatten").required("Verplicht"),
  firstName: yup.string().required("Verplicht"),
  lastName: yup.string().required("Verplicht"),
  pronounsMulti: yup.array().of(yup.string()).required("Verplicht"),
  pronounsOther: yup.string().when("pronounsMulti", {
    is: (pronounsMulti?: string[]) => pronounsMulti?.includes("Anders, namelijk"),
    then: (schema) => schema.required("Verplicht"),
    otherwise: (schema) => schema.nullIfEmpty(),
  }),
  dateOfBirth: yup.string().required("Verplicht"),
  telephone: yup.string().required("Verplicht"),
  address: yup.string().required("Verplicht"),
  postcode: yup.string().required("Verplicht"),
  residence: yup.string().required("Verplicht"),
  region: yup.string().required("Verplicht"),
  school: yup.string().required("Verplicht"),
  interests: yup.array().of(yup.string()).min(1, "Verplicht").required("Verplicht"),
  howDidYouHearMulti: yup.array().of(yup.string()).required("Verplicht"),
})

export function RegisterForm() {
  const client = useApolloClient()
  const router = useRouter()

  const [register, { loading }] = useRegisterMutation()
  const extraInfoModalProps = useDisclosure()
  const ageCheckModalProps = useDisclosure()
  const redirect = router.query[REDIRECT_PATH] as string | undefined

  const { data } = useGetUpcomingFinalDateQuery()
  const finalDate = dayjs(data?.upcomingFinalDate)

  const defaultValues = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    pronounsMulti: [] as string[],
    pronounsOther: "",
    telephone: "",
    region: "",
    residence: "",
    address: "",
    postcode: "",
    dateOfBirth: "",
    school: "",
    interests: [],
    isOver16: "",
    parentEmail: "",
    isPrivacyCheckAccepted: "",
    isParentNotified: "",
    howDidYouHearMulti: [],
  }

  const form = useForm({ schema: RegisterSchema, defaultValues })
  const isPronounsOther = form.watch("pronounsMulti")?.includes("Anders, namelijk")

  const handleRegister = (values: RegisterExtraInfo) => {
    const userFormValues = form.getValues()
    const submitValues: RegisterInput = {
      email: userFormValues.email,
      password: userFormValues.password,
      firstName: userFormValues.firstName,
      lastName: userFormValues.lastName,
      pronounsMulti: userFormValues.pronounsMulti,
      pronounsOther: isPronounsOther ? userFormValues.pronounsOther : undefined,
      telephone: userFormValues.telephone,
      region: userFormValues.region,
      residence: userFormValues.residence,
      address: userFormValues.address,
      postcode: userFormValues.postcode,
      dateOfBirth: userFormValues.dateOfBirth,
      school: userFormValues.school,
      isOver16: values.isOver16,
      parentEmail: values.isParentNotifiedDisabled ? null : values.parentEmail,
      isPrivacyCheckAccepted: values.isPrivacyCheckAccepted,
      isParentNotified: !values.isParentNotifiedDisabled,
      howDidYouHearMulti: userFormValues.howDidYouHearMulti,
    }

    return form.handler(() => register({ variables: { data: submitValues } }), {
      onSuccess: async (res) => {
        extraInfoModalProps.onClose()
        localStorage.setItem(ACCESS_TOKEN, res.register.token)
        await fetch("/api/login", {
          method: "post",
          body: JSON.stringify({
            [REFRESH_TOKEN_KEY]: res.register.refreshToken,
          }),
        })
        client.writeQuery({ query: MeDocument, data: { me: res.register.user } })
        router.replace(redirect || `/users/${res.register.user.id}`)
      },
    })
  }

  const handleAgeCheck = () => {
    if (dayjs.utc(form.getValues().dateOfBirth).add(20, "year").isBefore(finalDate)) {
      ageCheckModalProps.onOpen()
    } else {
      extraInfoModalProps.onOpen()
    }
  }
  return (
    <>
      <Form onSubmit={handleAgeCheck} {...form} id="register-form">
        <Stack spacing={2} p={2}>
          <Input name="email" label="Emailadres" placeholder="ryan@email.com" />
          <Input name="password" label="Wachtwoord" type="password" placeholder="********" />
          <Input name="firstName" label="Voornaam" placeholder="Ryan" />
          <Input name="lastName" label="Achternaam" placeholder="De Jong" />
          <MultiSelect name="pronounsMulti" label="Wat zijn je voornaamwoorden?" options={pronounOptions} />
          {isPronounsOther && <Input name="pronounsOther" label="Andere voornaamwoorden" />}
          <DateInput name="dateOfBirth" label="Geboortedatum" />
          <Input name="telephone" label="Telefoon" placeholder="06-12345678" />
          <Input name="address" label="Adres" placeholder="Straatnaam huisnummer" />
          <Input name="postcode" label="Postcode" placeholder="1234AB" />
          <Input name="residence" label="Woonplaats" placeholder="Amsterdam" />
          <Select name="region" label="Regio" options={regions} placeholder="Kies" />
          <Input name="school" label="School" helpText="Op welke school zit je?" />
          <MultiSelect name="interests" label="Interesses" options={categoryOptions} />
          <MultiSelect
            name="howDidYouHearMulti"
            label="Hoe heb je over ons gehoord?"
            options={howDidYourHearMultiOptions}
            isRequired
          />
          <Button colorScheme="yellow" type="submit" w="100%" isLoading={loading} isDisabled={loading}>
            Doe mee
          </Button>
          <FormError />
        </Stack>
      </Form>
      <Modal {...ageCheckModalProps}>
        <RegisterAgeCheckMessage
          openExtraInfoModal={extraInfoModalProps.onOpen}
          onClose={ageCheckModalProps.onClose}
        />
      </Modal>
      <Modal {...extraInfoModalProps} size="2xl">
        <RegisterExtraInfoForm handleRegister={handleRegister} />
      </Modal>
    </>
  )
}
